import { FormEvent, RefObject, useCallback, useEffect, useMemo, useState } from 'react'
import { isEmail } from '../../../../lib/utils'
import { signupUserTrial } from '../../../../lib/utils/api'
import getDomainFromEmail from '../../../../lib/utils/getDomainFromEmail'
import useEventTracking from '../../../../lib/hooks/tracking'
import { useRegistrationContext } from '../../context/registration'
import { useSignupContext } from '../../context/signup'
import { useSignupProductOptions } from '../../hooks/useSignupProductOptions'
import { productCategoryMap } from '../../../../lib/constants'
import { snakeCase } from 'lodash'

type ProductOption = {
  name: string
  value: string
}

type ProductOptions = ProductOption[]

export const useRegistrationLinkHandler = (ref: RefObject<HTMLElement>, params: any) => {
  const { goto } = useRegistrationContext()

  useEffect(() => {
    const linkClickHandler = e => {
      e.preventDefault()
      goto('/login', params)
    }
    if (ref.current) {
      const links = ref.current.querySelectorAll('a')
      links.forEach(link => {
        link.addEventListener('click', linkClickHandler)
      })
    }

    return () => {
      if (ref.current) {
        const links = ref.current.querySelectorAll('a')
        links.forEach(link => {
          link.removeEventListener('click', linkClickHandler)
        })
      }
    }
  }, [goto, params])
}

export type RegistrationFormHookOptions = {
  productName: string
  productOptions: ProductOptions
  productVariant: string
  operatingSystem: string
}

export const useRegistrationForm = () => {
  const [signupLoading, setSignupLoading] = useState(false)
  const { goto, setIsAuthenticated, isAuthenticated } = useRegistrationContext()
  const [error, setError] = useState<string | undefined>(undefined)
  const {
    productName,
    productVariant,
    operatingSystem,
    email,
    message,
    companyName,
    businessRole,
    acceptTerms,
    acceptNewsletter
  } =
    useSignupContext();

  const trackEvent = useEventTracking()
  const { operatingSystemOptions, productOptions, productVariantOptions } = useSignupProductOptions({
    operatingSystem: operatingSystem,
    productShortCode: productName,
    productVariant: productVariant,
  })
  const currentOption = useMemo(() => productOptions.find(option => option.value === productName), [productOptions])

  const isFormValid = useMemo(() => {
    return (
      !!email &&
      !!businessRole &&
      isEmail(email) &&
      acceptTerms &&
      productName &&
      productVariant
    )
  }, [email, businessRole, isEmail, acceptTerms, productName, productVariantOptions.length, productVariant])

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault()
      try {
        if (!isFormValid) return;
        await signupUserTrial(
          {
            email,
            acceptNewsletter,
            businessRole,
            companyName,
            message,
          },
          {
            os: operatingSystem,
            platform: productVariant,
            product: productName,
          },
        )

        setSignupLoading(false)
        setIsAuthenticated(true)

        const readableProductName = productOptions.find(option => option.value === productName)?.name || 'NA'
        const productCategory = productCategoryMap[snakeCase(readableProductName)] || 'NA'

        if (isAuthenticated) {
          goto('/downloads')
        } else {
          trackEvent('user_signed_up', {
            product_os: operatingSystemOptions.find(option => option.code === operatingSystem),
            product_name: readableProductName,
            product_category: productCategory,
            user_email: getDomainFromEmail(email),
            user_business_role: businessRole,
            user_company: companyName || 'NA',
            user_mkt_com_consent: acceptNewsletter ? 'true' : 'false',
            user_first_referral: message,
            user_type: 'reg',
          })
  
          goto('/downloads')
        }
      } catch (e) {
        setError(e.message)
        setSignupLoading(false)
      }
    },
    [
      productName,
      operatingSystem,
      email,
      acceptNewsletter,
      businessRole,
      companyName,
      message,
      productVariant,
      operatingSystem,
      setIsAuthenticated,
      goto,
      isFormValid,
    ],
  )




  return { currentOption, handleSubmit, isValid: isFormValid, loading: signupLoading, error }
}
